<script lang="ts">
	import PreloadedPrice from "@components/PreloadedPrice.svelte";
	import ProductB2bCard from "@components/ProductB2bCard.svelte";
	import type { ProductForCard } from "@components/ProductCard.js";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { Currency } from "@core/schema/Currency.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { getWeightText } from "@core/schema/utils/getWeightText.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./ProductB2bListingGrid.translate.json" with { type: "json" };

	const {
		products,
		subcategories,
	}: {
		products: ProductForCard[];
		subcategories: SubcategoryForCustomer[];
	} = $props();

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	const productCountsByWeightInGrams = $derived(
		Cart.orderProducts.reduce<Record<number, number>>((acc, orderProduct) => {
			if (orderProduct.info.variantType !== ProductVariantType.Weight) {
				return acc;
			}
			const { weightInGrams } = orderProduct.info.variant;
			acc[weightInGrams] = (acc[weightInGrams] ?? 0) + orderProduct.quantity;
			return acc;
		}, {}),
	);
</script>

<div class="grid grid-cols-1 xl:grid-cols-[1fr_auto_min-content_min-content] gap-y-8 xl:gap-y-4">
	{#each products as product (product)}
		{@const subcategory = subcategories.find(({ id }) => id === product.subcategoryId)}
		{#if subcategory}
			<ProductB2bCard {product} {subcategory} />
		{/if}
	{:else}
		<div class="text-fcrdarkgray col-span-full text-center my-5">
			<Formatted text={translate("noProducts")} />
		</div>
	{/each}
</div>
{#if Cart.itemCount > 0}
	<div
		class="fixed bottom-0 inset-x-0 z-20 grid-container bg-white shadow-[0_0.25rem_3rem_0_rgba(0,0,0,0.08)] border-t border-fcrlightgray-300"
	>
		<div class="flex items-center justify-between py-2 gap-2">
			<div class="flex items-center gap-2 xl:gap-5 font-antipol text-4.5/normal">
				<div class="text-fcrgray font-medium hidden lg:block">
					<Formatted text={translate("selected")} />
				</div>
				{#each Object.entries(productCountsByWeightInGrams) as [weightInGrams, count] (weightInGrams)}
					<div class="font-bold">
						<span class="text-fcrbrown"><Formatted text="{count}×" /></span>
						<span class="text-fcrdarkgray">
							<Formatted text={getWeightText(tenant.locale, parseInt(weightInGrams, 10))} />
						</span>
					</div>
				{/each}
			</div>
			<PreloadedPrice class="size-6 shrink-0">
				{#snippet children(currency: Currency)}
					<div class="flex flex-col xl:flex-row xl:gap-2 xl:items-baseline text-right font-medium shrink-0">
						<div>
							{#if currency === Currency.CZK}
								<Formatted
									text={formatPrice(Math.ceil(Cart.totalPrice?.withoutTax ?? 0), currency, tenant.locale, false)}
								/>
							{:else}
								<Formatted text={formatPrice(Cart.totalPrice?.withoutTax ?? 0, currency, tenant.locale, true)} />
							{/if}
						</div>
						<div class="text-fcrgray text-3/normal xl:[font-size:inherit] font-normal">
							{#if currency === Currency.CZK}
								<Formatted
									text={formatPrice(Math.ceil(Cart.totalPrice?.withTax ?? 0), currency, tenant.locale, false)}
								/>
							{:else}
								<Formatted text={formatPrice(Cart.totalPrice?.withTax ?? 0, currency, tenant.locale, true)} />
							{/if}
							<span><Formatted text={translate("withTax")} /></span>
						</div>
					</div>
				{/snippet}
			</PreloadedPrice>
			<div class="hidden xl:block">
				<Button
					disabled={Cart.itemCount === 0}
					variant={ButtonVariant.Dark}
					size={ButtonSize.Large}
					onclick={(): void => {
						Cart.showModal = true;
					}}
				>
					<Formatted text={translate("continueToCart")} />
				</Button>
			</div>
			<div class="block xl:hidden">
				<Button
					disabled={Cart.itemCount === 0}
					variant={ButtonVariant.Dark}
					size={ButtonSize.Medium}
					onclick={(): void => {
						Cart.showModal = true;
					}}
				>
					<Formatted text={translate("continueToCartShort")} />
				</Button>
			</div>
		</div>
	</div>
{/if}
